









































































import { Component, Prop, Vue } from 'vue-property-decorator';
import { VatsApi } from '@/api';
import moment from 'moment-timezone';
import DatePicker from '@/components/form pickers/DatePicker.vue';
import { DateRange } from '@/models/date-range';
import { ApiUtils } from '@/common/ApiUtils';
import { TooltipLabelBuilder } from '@/pages/account/products/feed/util/tooltip-label-builder';
import { Bullet, Circle, Color, Label, Legend, percent, Root, Tooltip } from '@amcharts/amcharts5';
import { XYChart } from '@amcharts/amcharts5/.internal/charts/xy/XYChart';
import { ValueAxis } from '@amcharts/amcharts5/.internal/charts/xy/axes/ValueAxis';
import { LineSeries } from '@amcharts/amcharts5/.internal/charts/xy/series/LineSeries';
import { AxisRendererX } from '@amcharts/amcharts5/.internal/charts/xy/axes/AxisRendererX';
import { AxisRendererY } from '@amcharts/amcharts5/.internal/charts/xy/axes/AxisRendererY';
import { XYCursor } from '@amcharts/amcharts5/.internal/charts/xy/XYCursor';
import { VatModels } from '@/common/EmptyDistanceUtils';
import { VolumeConversionUtils } from '@/common/VolumeConversionUtils';
import { Vat, VatCalibration } from '@/api';
import Toast from '@/components/display/toast.vue';
import ConfirmModal from '@/components/display/confirm-modal.vue';
import { VatService } from '@/pages/account/products/milk/vats/service/vat-service';

interface CollectionPoint {
	distance_mm: number;
	levno_volume: number;
	supplier_volume: number;
	supplier_timestamp: number;
	supplier_date: string;
	opacity: number;
}

@Component({
	components: { DatePicker, ConfirmModal },
})
export default class VatCalibrationScatter extends Vue {
	@Prop({ required: true, type: Object })
	public vat!: Vat;

	public loading: boolean = true;
	public errorMessage: string | null = null;
	public chartData: CollectionPoint[] = [];
	public calibration: VatCalibration | null = null;

	public isEditingExcludeBefore: boolean = false;
	public excludeBeforeDraft: string | null = null;
	public savingExcludeBefore: boolean = false;

	// Use 90-day date range by default. No real reason, felt like a good time span
	private startDate: moment.Moment = moment().startOf('day').subtract(90, 'days');
	private endDate: moment.Moment = moment().startOf('day').add(1, 'days');

	private vatsApi: VatsApi = new VatsApi();
	private root: Root | null = null;
	private readonly toast: Toast = new Toast();
	private readonly vatService: VatService = new VatService();

	public get chartId(): string {
		return `vatCalibrationScatter-${this.vat.id}`;
	}

	public get calibrationTimestamp(): string | null {
		if (!this.calibration) return null;
		return moment.unix(this.calibration.timestamp).tz('Pacific/Auckland').format('DD MMM YYYY HH:mm');
	}

	public get excludeBeforeFormatted(): string {
		if (this.calibration?.excludeBefore == null) return 'Not set';
		return moment.unix(this.calibration.excludeBefore).tz('Pacific/Auckland').format('DD MMM YYYY');
	}

	public get excludeBeforeConfirmModalId(): string {
		return `exclude-before-confirm-${this.vat.id}`;
	}

	public get excludeBeforeCurrentDraft(): string | null {
		if (this.calibration?.excludeBefore == null) return null;
		return moment.unix(this.calibration.excludeBefore).tz('Pacific/Auckland').format('YYYY-MM-DD');
	}

	public get isExcludeBeforeDraftUnchanged(): boolean {
		return (this.excludeBeforeDraft || null) === this.excludeBeforeCurrentDraft;
	}

	public get maxExcludeBeforeDate(): string {
		return moment().tz('Pacific/Auckland').format('YYYY-MM-DD');
	}

	public get excludeBeforeConfirmMessage(): string {
		if (!this.excludeBeforeDraft) {
			return 'This will clear the exclude-before cutoff and trigger a recalibration for this vat. Continue?';
		}
		const formatted = moment.tz(this.excludeBeforeDraft, 'YYYY-MM-DD', 'Pacific/Auckland').format('DD MMM YYYY');
		return `This will set the exclude-before cutoff to ${formatted} and trigger a recalibration for this vat. Continue?`;
	}

	public async created(): Promise<void> {
		await Promise.all([this.fetchData(), this.fetchCalibration()]);
		this.applyOpacity();
		if (this.chartData.length > 0) {
			this.$nextTick(() => this.createChart());
		}
	}

	public beforeDestroy(): void {
		this.root?.dispose();
	}

	public async onDateRangeChanged(newDateRange: Omit<DateRange, 'shortcut'>): Promise<void> {
		if (!newDateRange.end) return;
		this.startDate = moment(newDateRange.start, 'DD-MM-YYYY');
		this.endDate = moment(newDateRange.end, 'DD-MM-YYYY');
		await this.fetchData();
		this.applyOpacity();
		if (this.chartData.length > 0) {
			this.$nextTick(() => this.createChart());
		} else {
			this.root?.dispose();
			this.root = null;
		}
	}

	private async fetchData(): Promise<void> {
		this.loading = true;
		this.errorMessage = null;
		try {
			const response = await this.vatsApi.getVatCollectionsVsDistance(
				this.vat.id,
				this.startDate.unix(),
				this.endDate.unix(),
			);
			this.chartData = (response.data as CollectionPoint[])
				.filter(d => d.distance_mm != null && d.levno_volume != null && d.supplier_volume != null)
				.sort((a, b) => b.supplier_timestamp - a.supplier_timestamp)
				.map(d => ({
					...d,
					supplier_date: moment.unix(d.supplier_timestamp).tz('Pacific/Auckland').format('DD MMM YYYY HH:mm'),
				}));
		} catch (e) {
			const { errorMessage } = ApiUtils.handleApiError(e, 'Failed to load collection data');
			this.errorMessage = errorMessage;
			this.chartData = [];
		} finally {
			this.loading = false;
		}
	}

	private async fetchCalibration(): Promise<void> {
		try {
			const response = await this.vatsApi.getVatCalibration(this.vat.id);
			this.calibration = this.isCalibrationComplete(response.data.data) ? response.data.data : null;
		} catch (e) {
			const { errorMessage } = ApiUtils.handleApiError(e, 'Failed to load vat calibration');
			console.error(errorMessage, e);
			this.calibration = null;
		}
	}

	private isCalibrationComplete(data: VatCalibration | null | undefined): data is VatCalibration {
		return (
			data != null &&
			data.distanceToBottomOfVat != null &&
			data.detectedAdjustment != null &&
			data.residualAdjustment != null
		);
	}

	public startEditExcludeBefore(): void {
		this.excludeBeforeDraft = this.excludeBeforeCurrentDraft;
		this.isEditingExcludeBefore = true;
	}

	public cancelEditExcludeBefore(): void {
		this.isEditingExcludeBefore = false;
		this.excludeBeforeDraft = null;
	}

	public openExcludeBeforeConfirm(): void {
		this.$bvModal.show(this.excludeBeforeConfirmModalId);
	}

	public async confirmExcludeBefore(params: { modalId?: string }): Promise<void> {
		this.savingExcludeBefore = true;
		const excludeBefore = this.excludeBeforeDraft
			? moment.tz(this.excludeBeforeDraft, 'YYYY-MM-DD', 'Pacific/Auckland').startOf('day').unix()
			: null;
		const result = await this.vatService.updateVatCalibrationExcludeBefore(this.vat.id, excludeBefore);
		this.savingExcludeBefore = false;

		if (result.errorMessage) {
			this.toast.makeToast(result.errorMessage, 'Error', 'danger', true);
			return;
		}

		this.calibration = this.isCalibrationComplete(result.calibration) ? result.calibration : null;
		this.isEditingExcludeBefore = false;
		if (params?.modalId) this.$bvModal.hide(params.modalId);
		this.toast.makeToast('Exclude-before cutoff updated and vat recalibrated', 'Vat', 'success');
		if (this.chartData.length > 0) {
			this.$nextTick(() => this.createChart());
		}
	}

	private applyOpacity(): void {
		const minOpacity = 0.1;
		const step = (1 - minOpacity) / (this.chartData.length - 1 || 1);
		this.chartData = this.chartData.map((d, i) => ({ ...d, opacity: 1 - step * i }));
	}

	private makeSeries(
		root: Root,
		xAxis: ValueAxis<AxisRendererX>,
		yAxis: ValueAxis<AxisRendererY>,
		name: string,
		valueYField: string,
		color: Color,
		tooltipHeader: string,
	): LineSeries {
		const tooltip = Tooltip.new(root, {
			getFillFromSprite: false,
			labelText: new TooltipLabelBuilder()
				.line(tooltipHeader, { fontSize: 12 })
				.line('{valueY} L at {valueX} mm', { fontSize: 14, bold: true })
				.line('{supplier_date}', { color: '#4a4a4a', fontSize: 10 })
				.build(),
		});
		tooltip.get('background')?.setAll({ fill: color, fillOpacity: 0.8, strokeOpacity: 0 });

		const series = LineSeries.new(root, {
			name,
			xAxis,
			yAxis,
			valueXField: 'distance_mm',
			valueYField,
			stroke: color,
			fill: color,
			tooltip,
		});
		series.strokes.template.setAll({ strokeOpacity: 0 });
		series.bullets.push((r, _s, dataItem) =>
			Bullet.new(r, {
				sprite: Circle.new(r, {
					radius: 5,
					fill: color,
					fillOpacity: (dataItem.dataContext as CollectionPoint).opacity,
					stroke: color,
					strokeOpacity: 1,
					strokeWidth: 1,
				}),
			})
		);
		return series;
	}

	private addLineSeries(
		root: Root,
		xAxis: ValueAxis<AxisRendererX>,
		yAxis: ValueAxis<AxisRendererY>,
		chart: XYChart,
		data: Record<string, unknown>[],
		config: { name: string; valueYField: string; color: Color; strokeWidth: number; strokeOpacity: number; strokeDasharray?: number[] },
	): void {
		const series = LineSeries.new(root, {
			name: config.name,
			xAxis,
			yAxis,
			valueXField: 'distance_mm',
			valueYField: config.valueYField,
			stroke: config.color,
			fill: config.color,
		});
		series.strokes.template.setAll({
			strokeWidth: config.strokeWidth,
			strokeOpacity: config.strokeOpacity,
			...(config.strokeDasharray ? { strokeDasharray: config.strokeDasharray } : {}),
		});
		chart.series.push(series);
		series.data.setAll(data);
	}

	private getModelAttributes() {
		if (!this.vat.formula || !this.vat.sizeString) return null;
		const attrs = VatModels.get(`${this.vat.formula}:${this.vat.sizeString}`);
		if (!attrs) return null;
		const mmToMetres = (mm: number) => mm / 1000;
		return {
			slopeOffset: mmToMetres(attrs.slopeOffset),
			diameter: mmToMetres(attrs.diameter),
			height: mmToMetres(this.vat.height ?? attrs.height),
			ventDistance: mmToMetres(this.vat.ventDistance ?? attrs.ventDistance),
			offset: mmToMetres(this.vat.vatOffset ?? attrs.defaultOffset ?? 0),
		};
	}

	// Vertical-cylinder-with-sloped-base geometry, sampled at 3 points (0, the slope transition, xMax)
	// since the shape is piecewise linear either side of the transition.
	private calculateSlopedCylinderVolumes(
		distanceToBottomM: number,
		xMax: number,
		params: { slopeOffset: number; diameter: number; ventDistance: number },
	): { distance_mm: number; volume: number }[] {
		const { slopeOffset, diameter, ventDistance } = params;
		const radius = diameter / 2;
		// Delegate the actual volume formula to the shared utility rather than re-deriving it here.
		const calcVolume = (distanceMm: number) =>
			parseFloat(
				VolumeConversionUtils.fonterraVolumeFromDistance(
					distanceMm,
					0,
					slopeOffset,
					diameter,
					radius,
					distanceToBottomM,
					ventDistance,
				),
			);
		const tanAngle = slopeOffset / diameter; // tan(atan(x)) === x
		const slopeTransitionMm = (distanceToBottomM + ventDistance * tanAngle - slopeOffset) * 1000;
		return [
			{ distance_mm: 0, volume: calcVolume(0) },
			{ distance_mm: slopeTransitionMm, volume: calcVolume(slopeTransitionMm) },
			{ distance_mm: xMax, volume: calcVolume(xMax) },
		];
	}

	// Mirrors ingestion's applyVcc: at or beyond the vat's calibrated bottom, volume is forced to
	// exactly 0 rather than run through the scale/offset transform (which wouldn't reach 0 on its
	// own, since the sloped-base geometry still has a small positive volume at that point).
	private applyCalibrationAdjustment(distanceMm: number, volume: number, calibration: VatCalibration): number {
		if (distanceMm >= calibration.distanceToBottomOfVat) return 0;
		return Math.max(0, Math.round(calibration.detectedAdjustment * volume + calibration.residualAdjustment));
	}

	private calculateClassicFormulaVolumes(
		xMax: number,
		params: { slopeOffset: number; diameter: number; height: number; ventDistance: number; offset: number },
	): { distance_mm: number; model_volume: number }[] {
		const { height, offset, ...geometry } = params;
		return this.calculateSlopedCylinderVolumes(height + offset, xMax, geometry).map(({ distance_mm, volume }) => ({
			distance_mm,
			model_volume: Math.round(volume),
		}));
	}

	// Same geometry as calculateClassicFormulaVolumes, but anchored on the calibration-detected
	// distanceToBottomOfVat and with detectedAdjustment/residualAdjustment applied on top.
	private calculateNewFormulaVolumes(
		xMax: number,
		params: { slopeOffset: number; diameter: number; ventDistance: number },
		calibration: VatCalibration,
	): { distance_mm: number; new_formula_volume: number }[] {
		const { distanceToBottomOfVat, detectedAdjustment, residualAdjustment } = calibration;
		if (distanceToBottomOfVat == null || detectedAdjustment == null || residualAdjustment == null) return [];
		// Sample only up to distanceToBottomOfVat, since the curve hits exactly 0 there and stays
		// flat beyond it (per applyCalibrationAdjustment's guard) rather than continuing the slope.
		const points = this.calculateSlopedCylinderVolumes(distanceToBottomOfVat / 1000, Math.min(xMax, distanceToBottomOfVat), params).map(
			({ distance_mm, volume }) => ({
				distance_mm,
				new_formula_volume: this.applyCalibrationAdjustment(distance_mm, volume, calibration),
			}),
		);
		if (xMax > distanceToBottomOfVat) {
			points.push({ distance_mm: xMax, new_formula_volume: 0 });
		}
		return points;
	}

	private createChart(): void {
		this.root?.dispose();
		this.root = Root.new(this.chartId);
		const chart = XYChart.new(this.root, {});

		const dataMaxDistance = Math.max(...this.chartData.map(d => d.distance_mm));
		const dataMaxVolume = Math.max(...this.chartData.map(d => d.supplier_volume));
		const xMax = Math.max(
			dataMaxDistance,
			this.vat.emptyDistance ?? 0,
			this.calibration?.distanceToBottomOfVat ?? 0,
		);
		const parsedSize = this.vat.sizeString != null ? parseInt(this.vat.sizeString) : NaN;

		const modelParams = this.getModelAttributes();
		const modelData = modelParams ? this.calculateClassicFormulaVolumes(xMax, modelParams) : [];
		const newFormulaData =
			modelParams && this.calibration ? this.calculateNewFormulaVolumes(xMax, modelParams, this.calibration) : [];

		const yMax = !isNaN(parsedSize) ? Math.max(parsedSize, dataMaxVolume) : dataMaxVolume;

		const xAxis = ValueAxis.new(this.root, {
			renderer: AxisRendererX.new(this.root, {}),
			tooltip: Tooltip.new(this.root, {}),
			min: 0,
			max: xMax,
		});

		const yAxis = ValueAxis.new(this.root, {
			renderer: AxisRendererY.new(this.root, {}),
			tooltip: Tooltip.new(this.root, {}),
			min: 0,
			max: yMax,
		});

		xAxis.get('renderer').labels.template.setAll({ fontFamily: 'sofia-pro-soft' });
		yAxis.get('renderer').labels.template.setAll({ fontFamily: 'sofia-pro-soft' });

		xAxis.children.push(Label.new(this.root, {
			text: 'Sensor Distance (mm)',
			x: percent(50),
			centerX: percent(50),
			fontFamily: 'sofia-pro-soft',
		}));

		yAxis.children.moveValue(Label.new(this.root, {
			text: 'Volume (L)',
			y: percent(50),
			centerX: percent(50),
			rotation: -90,
			fontFamily: 'sofia-pro-soft',
		}), 0);

		const levnoSeries = this.makeSeries(this.root, xAxis, yAxis, 'Levno Volume (L)', 'levno_volume', Color.fromHex(0xff7a00), 'Levno Volume');
		const supplierSeries = this.makeSeries(this.root, xAxis, yAxis, 'Supplier Volume (L)', 'supplier_volume', Color.fromHex(0x2979ff), 'Supplier Volume');

		const legend = Legend.new(this.root, {
			x: percent(50),
			centerX: percent(50),
		});

		const cursor = XYCursor.new(this.root, { behavior: 'zoomXY' });

		chart.set('cursor', cursor);
		chart.plotContainer.events.on('dblclick', () => chart.zoomOut());
		chart.xAxes.push(xAxis);
		chart.yAxes.push(yAxis);
		chart.series.push(levnoSeries);
		chart.series.push(supplierSeries);

		if (modelData.length > 0) {
			// Only fade/dash the classic line when the new calibrated line is also shown, so it
			// recedes in favour of the more prominent one — otherwise it's the only reference line
			// on the chart and should stay fully visible.
			const hasNewFormulaLine = newFormulaData.length > 0;
			this.addLineSeries(this.root, xAxis, yAxis, chart, modelData, {
				name: 'Classic Formula Volume',
				valueYField: 'model_volume',
				color: Color.fromHex(0x2ca02c),
				strokeWidth: 2,
				strokeOpacity: hasNewFormulaLine ? 0.5 : 1,
				strokeDasharray: hasNewFormulaLine ? [6, 4] : undefined,
			});
		}

		if (newFormulaData.length > 0) {
			this.addLineSeries(this.root, xAxis, yAxis, chart, newFormulaData, {
				name: 'New Formula',
				valueYField: 'new_formula_volume',
				color: Color.fromHex(0xd62728),
				strokeWidth: 3,
				strokeOpacity: 1,
			});
		}

		chart.bottomAxesContainer.children.push(legend);

		levnoSeries.data.setAll(this.chartData);
		supplierSeries.data.setAll(this.chartData);
		legend.data.setAll(chart.series.values);

		this.root.container.children.push(chart);
		chart.appear(1000, 100);
	}
}
